// libraries.
import { Badge, Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

// styles.
import { WrapperIntersections } from '@domains/intersections/components/IntersectionsList/styles';

// api.
import {
  InvalidPolygon,
  InvalidPolygonReason,
  useGetInvalidPolygonsQuery,
} from '@domains/audit-polygons/store/api-slices/auditPolygons.endpoints';

// slices.
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { setMedianZoneCode } from '@domains/median-zones/store/slices/medianZones.slice';
import {
  getAuditRequested,
  setAuditRequested,
  setSelectedAuditPolygon,
} from '@domains/audit-polygons/store/slices/auditPolygons.slice';

const REASON_LABEL: Record<InvalidPolygonReason, string> = {
  self_intersection: 'Se corta a sí mismo',
  invalid_geometry: 'Geometría inválida',
  null_geometry: 'Sin geometría',
  unknown: 'Geometría con problemas',
};

export const AuditPolygonsTool = () => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry);
  const requested = useSelector(getAuditRequested);

  // El backend es la única fuente de verdad: la lista muestra exactamente los
  // polígonos que devuelve la auditoría (ya descarta los falsos positivos por
  // vértices duplicados). El front no re-valida geometría aquí.
  const { data, isFetching, isError, refetch } = useGetInvalidPolygonsQuery(
    { country },
    { skip: !requested }
  );

  const invalidPolygons = data ?? [];

  const handleAudit = () => {
    if (!requested) {
      dispatch(setAuditRequested(true));
    } else {
      refetch();
    }
  };

  // Dibuja el polígono seleccionado directo desde sus coordenadas y centra el
  // mapa en él (lo hace AuditSelectedPolygon). NO cambia de pestaña: el usuario
  // se queda en la auditoría y la lista se conserva. Si tiene zona mediana,
  // también la carga para dar contexto alrededor.
  const handleSelect = (polygon: InvalidPolygon) => {
    dispatch(setSelectedAuditPolygon(polygon));
    if (polygon.median_zone_id) {
      dispatch(setMedianZoneCode(polygon.median_zone_id.toString()));
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 75px)', overflowY: 'auto' }}>
      <WrapperIntersections>
        <div className="title p-3">Auditar polígonos</div>
        <div className="px-3 pb-2" style={{ fontSize: '14px' }}>
          Busca polígonos activos con geometría inválida (que se cortan a sí
          mismos u otras formas raras) para corregirlos. Haz clic en uno para
          verlo resaltado en el mapa.
        </div>
        <div className="px-3 pb-3">
          <Button
            className="btn-habi w-100"
            onClick={handleAudit}
            disabled={isFetching}
          >
            {isFetching ? 'Buscando...' : 'Buscar polígonos con errores'}
          </Button>
        </div>

        <div className="px-3 pb-3 d-flex flex-column gap-2">
          {isError && (
            <div style={{ color: '#DC3545', fontSize: '14px' }}>
              Ocurrió un error al auditar los polígonos. Intenta de nuevo.
            </div>
          )}

          {!isError &&
            requested &&
            !isFetching &&
            invalidPolygons.length === 0 && (
              <div style={{ fontSize: '14px' }}>
                No se encontraron polígonos con geometría inválida. 🎉
              </div>
            )}

          {!isError &&
            invalidPolygons.map(polygon => {
              const isBlocking = polygon.polygon_type === 'blocking';
              const hasZone = Boolean(polygon.median_zone_id);
              return (
                <Card
                  key={polygon.polygon_id}
                  role="button"
                  onClick={() => handleSelect(polygon)}
                  style={{
                    borderLeft: '4px solid #DC3545',
                    cursor: 'pointer',
                  }}
                >
                  <Card.Body className="p-2">
                    <div className="d-flex align-items-center gap-2">
                      <strong>ID: {polygon.polygon_id}</strong>
                      <Badge bg={isBlocking ? 'danger' : 'secondary'}>
                        {isBlocking ? 'Bloqueo' : 'Standard'}
                      </Badge>
                    </div>
                    <div style={{ color: '#DC3545', fontSize: '13px' }}>
                      {REASON_LABEL[polygon.reason] ?? REASON_LABEL.unknown}
                    </div>
                    {polygon.comment?.trim() && (
                      <div style={{ fontSize: '13px' }}>{polygon.comment}</div>
                    )}
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {hasZone
                        ? `Zona mediana: ${polygon.median_zone_id} — clic para verlo en el mapa`
                        : 'Sin zona mediana — clic para verlo en el mapa'}
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
        </div>
      </WrapperIntersections>
    </div>
  );
};
